import { useState, useEffect } from 'react';
import io from 'socket.io-client';
import { getStorage } from '../utils/localOrSessionStorage';
import { getEnv } from '../utils/env';

// Create socket instance with auth headers
const socket = io(getEnv('BACKEND_URL'), {
  autoConnect: false,
  transports: ['websocket'],
});

// One handle on the live socket, so a real-time surface can be driven from
// outside React — the E2E suite dispatches server events through the same
// listeners the app registers, rather than reaching past them into component
// state. Reading it is safe; it is the same instance this module exports.
declare global {
  interface Window {
    __homepointSocket?: typeof socket;
  }
}

if (typeof window !== 'undefined') {
  window.__homepointSocket = socket;
}

// Custom hook for socket state management
export const useSocket = () => {
  const [isConnected, setIsConnected] = useState(socket.connected);

  useEffect(() => {
    const onConnect = () => {
      setIsConnected(true);
      socket.emit(
        'create',
        'authentication',
        {
          strategy: 'jwt',
          accessToken: getStorage().getItem('jwtToken'),
        },
        function (error: any, _newAuthResult: any) {
          if (error) {
            console.error(error);
          }
        }
      );
    };

    const onDisconnect = (reason: string) => {
      setIsConnected(false);
      console.info('Socket disconnected:', reason);
    };

    const onConnectError = (error: any) => {
      setIsConnected(false);
      if (error.message === 'unauthorized') {
        console.error('Socket authentication failed');
        // Handle unauthorized error (e.g., redirect to login)
      } else {
        console.error('Socket connection error:', error);
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
    };
  }, []);

  return {
    socket,
    isConnected,
    connect: connectSocket,
    disconnect: disconnectSocket,
  };
};

// Function to connect socket with current auth token
export const connectSocket = () => {
  socket.connect();
};

// Function to disconnect socket
export const disconnectSocket = () => {
  socket.disconnect();
};

export default socket;
